import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { authClient } from "../../utils/amplifyClient";
import { getUrl, uploadData } from "aws-amplify/storage";
import { v4 as uuidv4 } from "uuid";
import * as Sentry from "@sentry/browser";

import { useUserContext } from "../../contexts/UserContext";
import { useUserModule, UserModuleProvider } from "../../contexts/UserModuleContext";
import { UserProfileTab } from "../../types/userModuleTypes";
import { canManageCompanyUser, getCompanyOwnerEmailFromDirectory } from "../../utils/permissions";
import { canViewOrgChartTab } from "../../utils/orgChartHierarchy";
import { generateChangeLogEntry } from "../../utils/userModuleUtils";
import { getSensitiveUserInfo } from "../../graphql/queries";
import {
    createSensitiveUserInfo as createSensitiveUserInfoMutation,
    updateUser as updateUserMutation,
    updateCompanyOwner as updateCompanyOwnerMutation,
    updateSensitiveUserInfo as updateSensitiveUserInfoMutation,
} from "../../graphql/mutations";
import { CompanyOwner, SensitiveUserInfo, User } from "../../types/schema";
import { S3_IMAGE_PATH } from "../../utils/constants";
import { resizeFile } from "../../utils/helperFunctions";
import { getGraphQlErrorMessage, isConditionalRequestFailure } from "../../utils/graphqlErrorUtils";
import { PersonalInfoTab } from "./tabs/PersonalInfoTab";
import { EmploymentTab } from "./tabs/EmploymentTab";
import { SafetyTrainingTab } from "./tabs/SafetyTrainingTab";
import { SkillsetsTab } from "./tabs/SkillsetsTab";
import { MediaFilesTab } from "./tabs/MediaFilesTab";
import { ChangeLogTab } from "./tabs/ChangeLogTab";
import { OrgChartTab } from "./tabs/OrgChartTab";
import { BackButton } from "../ui";

import "../../css/SingleUserProfilePage.css";

/**
 * Fields that exist on CompanyOwner and can be saved from the profile page.
 * Do not include role / email / subscriptionType / siteAdmin / cognitoId / companyId —
 * those identify the company owner and must not be changed (or cleared) from this UI.
 */
const COMPANY_OWNER_WRITABLE_FIELDS = new Set([
    'name',
    'domain',
    'permissions',
    'contactPhoneNumber',
    'discrepancyNotification',
    'discrepancyNotificationTypes',
    'pmNotification',
    'pmNotificationTypes',
    'trainingNotification',
    'trainingNotificationTypes',
]);

/** Never send these on updateCompanyOwner from profile save. */
const COMPANY_OWNER_PROTECTED_FIELDS = new Set([
    'role',
    'email',
    'subscriptionType',
    'siteAdmin',
    'cognitoId',
    'companyId',
]);

const COMPANY_OWNER_BOOLEAN_FIELDS = new Set([
    'discrepancyNotification',
    'pmNotification',
    'trainingNotification',
]);

const USER_BOOLEAN_FIELDS = new Set([
    ...COMPANY_OWNER_BOOLEAN_FIELDS,
    'globalLocationPermission',
    'driversLicenseStatus',
    'onHighwayEligibility',
]);

/**
 * Directory rows synthesized from CompanyOwner (no User row) carry subscriptionType.
 * Those must be saved via updateCompanyOwner, not updateUser.
 * Do not require role === 'owner' — owners are created as "super-admin".
 */
const isCompanyOwnerOnlyProfile = (user: User) =>
    'subscriptionType' in user &&
    (user as User & { subscriptionType?: string | null }).subscriptionType != null;

const coerceProfileFieldValue = (field: string, value: string): string | boolean => {
    if (USER_BOOLEAN_FIELDS.has(field) || field.endsWith('Notification')) {
        return value === 'true';
    }
    return value;
};

const TABS: { id: UserProfileTab; label: string; adminOnly?: boolean }[] = [
    { id: "personal", label: "Personal Info" },
    { id: "employment", label: "Employment" },
    { id: "safety-training", label: "Safety & Training" },
    { id: "skillsets", label: "Skillsets" },
    { id: "media-files", label: "Media & Files" },
    { id: "change-log", label: "Change Log" },
    { id: "org-chart", label: "Org. Chart", adminOnly: true },
];

const TAB_IDS = new Set<UserProfileTab>(TABS.map((tab) => tab.id));

const parseProfileTab = (value: string | null): UserProfileTab | null => {
    if (!value || !TAB_IDS.has(value as UserProfileTab)) return null;
    return value as UserProfileTab;
};

type ProfileFieldValue = string | boolean | string[];

/**
 * Represents a single field change tracked during editing.
 */
interface PendingChange {
    field: string;
    oldValue: ProfileFieldValue;
    newValue: ProfileFieldValue;
}

/** Fields accepted on updateUser from profile tabs (rejects typos like `district`). */
const USER_PROFILE_WRITABLE_FIELDS = new Set([
    'name',
    'email',
    'domain',
    'contactPhoneNumber',
    'contactPhoneType',
    'position',
    'currentJobClassification',
    'dateOfHire',
    'inputByPerson',
    'notesAboutUser',
    'userPhoto',
    'driversLicenseStatus',
    'onHighwayEligibility',
    'regionalAssignment',
    'locationAssignment',
    'globalLocationPermission',
    'discrepancyNotification',
    'discrepancyNotificationTypes',
    'pmNotification',
    'pmNotificationTypes',
    'trainingNotification',
    'trainingNotificationTypes',
]);

const USER_ARRAY_FIELDS = new Set(['regionalAssignment', 'locationAssignment']);

const toChangeLogValue = (value: ProfileFieldValue | null | undefined): string | null => {
    if (value == null) return null;
    if (Array.isArray(value)) return value.join(', ');
    return String(value);
};

const valuesEqual = (a: ProfileFieldValue, b: ProfileFieldValue): boolean => {
    if (Array.isArray(a) || Array.isArray(b)) {
        const left = Array.isArray(a) ? a : [];
        const right = Array.isArray(b) ? b : [];
        if (left.length !== right.length) return false;
        const sortedLeft = [...left].sort();
        const sortedRight = [...right].sort();
        return sortedLeft.every((item, index) => item === sortedRight[index]);
    }
    return a === b;
};

/**
 * Inner component that uses UserModuleContext
 */
const SingleUserProfilePageInner = () => {
    useNavigate();
    const { userId } = useParams<{ userId: string }>();
    const [searchParams, setSearchParams] = useSearchParams();
    const { allCompanyUsers, setAllCompanyUsers, companyUser, companyOwner, company, setCompanyUser, setCompanyOwner, businessAdmin } =
        useUserContext();
    const { fetchSkillAssignments, fetchUserFiles, fetchChangeLog, createChangeLogEntry } = useUserModule();

    const [activeTab, setActiveTab] = useState<UserProfileTab>(
        () => parseProfileTab(searchParams.get("tab")) || "personal",
    );
    const [orgChartVisited, setOrgChartVisited] = useState(
        () => parseProfileTab(searchParams.get("tab")) === "org-chart",
    );
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [profileUser, setProfileUser] = useState<User | null>(null);
    const [sensitiveInfo, setSensitiveInfo] = useState<SensitiveUserInfo | null>(null);
    const [photoUrl, setPhotoUrl] = useState("");
    const [pendingPhotoFile, setPendingPhotoFile] = useState<File | null>(null);
    const [pendingPhotoPreviewUrl, setPendingPhotoPreviewUrl] = useState('');

    // Track pending field changes during editing
    const pendingChangesRef = useRef<PendingChange[]>([]);

    // Find user from context
    useEffect(() => {
        if (userId && allCompanyUsers.length > 0) {
            const found = allCompanyUsers.find((u) => u.id === userId) || null;
            setProfileUser(found);

            if (found?.userPhoto) {
                loadPhoto(found.userPhoto);
            } else {
                setPhotoUrl("");
            }
        }
    }, [userId, allCompanyUsers]);

    useEffect(() => {
        if (!pendingPhotoFile) {
            return;
        }

        const url = URL.createObjectURL(pendingPhotoFile);
        setPendingPhotoPreviewUrl(url);

        return () => URL.revokeObjectURL(url);
    }, [pendingPhotoFile]);

    // Fetch user-specific data on mount
    useEffect(() => {
        if (userId) {
            fetchSkillAssignments(userId);
            fetchUserFiles(userId);
            fetchChangeLog(userId);
            fetchSensitiveInfo(userId);
        }
    }, [userId]);

    const fetchSensitiveInfo = async (id: string) => {
        try {
            const res = await authClient.graphql({
                query: getSensitiveUserInfo,
                variables: { id },
            });
            setSensitiveInfo((res as any).data.getSensitiveUserInfo as SensitiveUserInfo | null);
        } catch (err) {
            console.error("Error fetching sensitive user info:", err);
            Sentry.captureException(err);
        }
    };

    const loadPhoto = async (path: string) => {
        try {
            const result = await getUrl({ path });
            setPhotoUrl(result.url.href);
        } catch (err) {
            console.error("Error loading user photo:", err);
        }
    };

    const getInitials = (name?: string | null): string => {
        if (!name) return "?";
        const parts = name.trim().split(" ");
        if (parts.length >= 2) {
            return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
        }
        return (parts[0][0] || "?").toUpperCase();
    };

    const getRoleBadgeClass = (role?: string | null): string => {
        if (!role) return "default";
        const lower = role.toLowerCase();
        if (lower.includes("admin")) return "admin";
        if (lower.includes("operator")) return "operator";
        if (lower.includes("inspector")) return "inspector";
        if (lower.includes("owner")) return "owner";
        return "default";
    };

    const formatRoleName = (role?: string | null): string => {
        if (!role) return "—";
        return role
            .split("-")
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(" ");
    };

    const handleEditProfile = () => {
        pendingChangesRef.current = [];
        setPendingPhotoFile(null);
        setSaveError(null);
        setIsEditing(true);
    };

    const handleDiscard = () => {
        pendingChangesRef.current = [];
        setPendingPhotoFile(null);
        setSaveError(null);
        setIsEditing(false);
    };

    const handlePhotoSelect = useCallback((file: File) => {
        setPendingPhotoFile(file);
    }, []);

    const canEditThisProfile =
        profileUser &&
        canManageCompanyUser(
            companyUser,
            companyOwner,
            profileUser,
            company?.companyOwnerId,
            getCompanyOwnerEmailFromDirectory(allCompanyUsers, company?.companyOwnerId),
            businessAdmin,
        );

    const canViewOrgChart = canViewOrgChartTab({
        businessAdmin,
        companyOwner,
        role: companyUser?.role || companyOwner?.role,
    });

    const visibleTabs = useMemo(
        () => TABS.filter((tab) => !tab.adminOnly || canViewOrgChart),
        [canViewOrgChart],
    );

    const selectTab = useCallback(
        (tab: UserProfileTab) => {
            setActiveTab(tab);
            if (tab === "org-chart") {
                setOrgChartVisited(true);
            }
            setSearchParams(
                (prev) => {
                    const next = new URLSearchParams(prev);
                    if (tab === "personal") {
                        next.delete("tab");
                    } else {
                        next.set("tab", tab);
                    }
                    return next;
                },
                { replace: true },
            );
        },
        [setSearchParams],
    );

    const viewerIdentityReady = Boolean(businessAdmin || companyOwner || companyUser);

    // Keep tab in sync when navigating between users with ?tab=...
    useEffect(() => {
        const fromUrl = parseProfileTab(searchParams.get("tab")) || "personal";

        if (fromUrl === "org-chart") {
            if (!viewerIdentityReady) {
                setActiveTab("org-chart");
                setOrgChartVisited(true);
                return;
            }
            if (!canViewOrgChart) {
                setActiveTab("personal");
                setSearchParams(
                    (prev) => {
                        if (!prev.get("tab")) return prev;
                        const next = new URLSearchParams(prev);
                        next.delete("tab");
                        return next;
                    },
                    { replace: true },
                );
                return;
            }
            setOrgChartVisited(true);
        }

        setActiveTab(fromUrl);
    }, [searchParams, canViewOrgChart, viewerIdentityReady, setSearchParams]);

    const handleFieldChange = useCallback(
        (field: string, oldValue: ProfileFieldValue, newValue: ProfileFieldValue) => {
            // Deduplicate: if the same field was already changed, update the entry
            const existing = pendingChangesRef.current.find((c) => c.field === field);
            if (existing) {
                existing.newValue = newValue;
            } else {
                pendingChangesRef.current.push({ field, oldValue, newValue });
            }
        },
        [],
    );

    const handleSave = async () => {
        if (!profileUser || !userId) return;

        const currentUser = companyUser || companyOwner;
        if (!currentUser) return;

        const companyId = (companyUser?.companyId as string) || companyOwner?.companyId || "";
        const changedBy = currentUser.id;
        const changedByName = (currentUser as { name?: string | null }).name || "Admin";
        const savingCompanyOwnerOnly = isCompanyOwnerOnlyProfile(profileUser);

        setIsSaving(true);
        setSaveError(null);

        try {
            // Separate changes into User fields vs SensitiveUserInfo fields
            const sensitiveFields = new Set([
                "dateOfBirth",
                "emergencyContactName",
                "emergencyContactRelationship",
                "emergencyContactPhoneNumber",
                "driversLicenseNumber",
                "driversLicenseState",
                "driversLicenseClass",
                "driversLicenseExpirationDate",
            ]);

            const userUpdates: Record<string, string | boolean | string[]> = {};
            const companyOwnerUpdates: Record<string, string | boolean> = {};
            const sensitiveUpdates: Record<string, string> = {};
            const unsupportedOwnerFields: string[] = [];
            const unknownUserFields: string[] = [];
            const changesForLog: PendingChange[] = [];

            // Filter out changes where old === new (no actual change)
            const actualChanges = pendingChangesRef.current.filter(
                (c) => !valuesEqual(c.oldValue, c.newValue),
            );

            if (actualChanges.length === 0 && !pendingPhotoFile) {
                // Nothing changed — leave edit mode instead of showing an error.
                pendingChangesRef.current = [];
                setSaveError(null);
                setIsEditing(false);
                setIsSaving(false);
                return;
            }

            for (const change of actualChanges) {
                if (sensitiveFields.has(change.field)) {
                    sensitiveUpdates[change.field] = String(change.newValue);
                    continue;
                }

                if (USER_ARRAY_FIELDS.has(change.field)) {
                    const arrayValue = Array.isArray(change.newValue)
                        ? change.newValue
                        : String(change.newValue)
                              .split(',')
                              .map((part) => part.trim())
                              .filter(Boolean);

                    if (savingCompanyOwnerOnly) {
                        unsupportedOwnerFields.push(change.field);
                        continue;
                    }
                    if (!USER_PROFILE_WRITABLE_FIELDS.has(change.field)) {
                        unknownUserFields.push(change.field);
                        continue;
                    }
                    userUpdates[change.field] = arrayValue;
                    continue;
                }

                const coerced = coerceProfileFieldValue(change.field, String(change.newValue));

                if (savingCompanyOwnerOnly) {
                    if (COMPANY_OWNER_WRITABLE_FIELDS.has(change.field)) {
                        companyOwnerUpdates[change.field] = coerced;
                    } else {
                        unsupportedOwnerFields.push(change.field);
                    }
                    continue;
                }

                if (!USER_PROFILE_WRITABLE_FIELDS.has(change.field)) {
                    unknownUserFields.push(change.field);
                    continue;
                }

                userUpdates[change.field] = coerced;
            }

            if (
                unknownUserFields.length > 0 &&
                Object.keys(userUpdates).length === 0 &&
                Object.keys(companyOwnerUpdates).length === 0 &&
                Object.keys(sensitiveUpdates).length === 0 &&
                !pendingPhotoFile
            ) {
                setSaveError(
                    `These fields cannot be saved on the user profile: ${unknownUserFields.join(', ')}.`,
                );
                setIsSaving(false);
                return;
            }

            // Keep CompanyOwner in sync when editing the account-owner profile
            // (even if a User row with the same id also exists).
            const isAccountOwnerProfile =
                userId === company?.companyOwnerId || userId === companyOwner?.id;
            if (isAccountOwnerProfile && !savingCompanyOwnerOnly) {
                for (const [field, value] of Object.entries(userUpdates)) {
                    if (COMPANY_OWNER_WRITABLE_FIELDS.has(field)) {
                        // COMPANY_OWNER_WRITABLE_FIELDS only contains string/boolean fields
                        // (never the array-typed user fields), so this value is string | boolean.
                        companyOwnerUpdates[field] = value as string | boolean;
                    }
                }
            }

            if (pendingPhotoFile) {
                if (savingCompanyOwnerOnly) {
                    setSaveError(
                        'Profile photos are not supported for company owner accounts yet. Save name and other owner fields without a photo.',
                    );
                    setIsSaving(false);
                    return;
                }

                const resizedImage = await resizeFile(pendingPhotoFile);
                const imageFile = resizedImage as File;
                const safeName = (profileUser.name || 'user').replace(/\s+/g, '-');
                const fileName = `${uuidv4()}-USER-${safeName}-${imageFile.name}`;
                const savedImage = await uploadData({
                    path: S3_IMAGE_PATH.userPhotoPath(companyId, userId, fileName),
                    data: imageFile,
                }).result;

                userUpdates.userPhoto = savedImage.path;
                changesForLog.push({
                    field: 'userPhoto',
                    oldValue: profileUser.userPhoto || '',
                    newValue: savedImage.path,
                });
            }

            if (
                savingCompanyOwnerOnly &&
                unsupportedOwnerFields.length > 0 &&
                Object.keys(companyOwnerUpdates).length === 0 &&
                Object.keys(sensitiveUpdates).length === 0
            ) {
                setSaveError(
                    `These fields are not stored on company owner accounts yet: ${unsupportedOwnerFields.join(', ')}.`,
                );
                setIsSaving(false);
                return;
            }

            changesForLog.push(
                ...actualChanges.filter((change) => {
                    if (sensitiveFields.has(change.field)) return true;
                    if (savingCompanyOwnerOnly) {
                        return COMPANY_OWNER_WRITABLE_FIELDS.has(change.field);
                    }
                    return true;
                }),
            );

            const mergedUserUpdates = { ...userUpdates, ...companyOwnerUpdates };

            // Persist CompanyOwner record changes (owner-only directory rows).
            // Strip identity fields so a profile save cannot demote/unlink the company owner.
            if (Object.keys(companyOwnerUpdates).length > 0) {
                const safeCompanyOwnerUpdates = Object.fromEntries(
                    Object.entries(companyOwnerUpdates).filter(
                        ([field]) => !COMPANY_OWNER_PROTECTED_FIELDS.has(field),
                    ),
                );

                if (Object.keys(safeCompanyOwnerUpdates).length > 0) {
                    await authClient.graphql({
                        query: updateCompanyOwnerMutation,
                        variables: {
                            input: {
                                id: userId,
                                ...safeCompanyOwnerUpdates,
                            },
                        },
                    });
                }
            }

            // Persist User record changes.
            // Do not send `owner` — UpdateUserInput rejects it (GraphQL validation error).
            if (Object.keys(userUpdates).length > 0) {
                try {
                    await authClient.graphql({
                        query: updateUserMutation,
                        variables: {
                            input: {
                                id: userId,
                                ...userUpdates,
                            },
                        },
                    });
                } catch (userUpdateErr) {
                    // Account owners sometimes lack a User row (CompanyOwner-only). Name/phone
                    // still save via updateCompanyOwner above — don't fail the whole save.
                    if (isConditionalRequestFailure(userUpdateErr) && isAccountOwnerProfile) {
                        console.warn(
                            'updateUser conditional failure for account owner; CompanyOwner write may still apply.',
                            userUpdateErr,
                        );
                        Sentry.captureException(userUpdateErr);
                    } else {
                        throw userUpdateErr;
                    }
                }
            }

            // Persist SensitiveUserInfo changes with upsert — get may miss an existing row
            // (auth/owner mismatch), and update fails when the row was never created.
            if (Object.keys(sensitiveUpdates).length > 0) {
                let existingSensitive = sensitiveInfo;
                if (!existingSensitive) {
                    try {
                        const existingRes = await authClient.graphql({
                            query: getSensitiveUserInfo,
                            variables: { id: userId },
                        });
                        existingSensitive =
                            ((existingRes as { data?: { getSensitiveUserInfo?: SensitiveUserInfo | null } }).data
                                ?.getSensitiveUserInfo as SensitiveUserInfo | null) ?? null;
                    } catch {
                        existingSensitive = null;
                    }
                }

                const sensitiveInput = { id: userId, ...sensitiveUpdates };

                const runUpdate = async () => {
                    await authClient.graphql({
                        query: updateSensitiveUserInfoMutation,
                        variables: { input: sensitiveInput },
                    });
                    setSensitiveInfo((prev) =>
                        prev
                            ? { ...prev, ...sensitiveUpdates }
                            : ({ id: userId, ...sensitiveUpdates } as SensitiveUserInfo),
                    );
                };

                const runCreate = async () => {
                    const targetCognitoId = profileUser.cognitoId;
                    if (!targetCognitoId || !companyId) {
                        throw new Error(
                            'Cannot create SensitiveUserInfo: missing cognitoId or companyId for target user.',
                        );
                    }
                    // Do not send `owner` — CreateSensitiveUserInfoInput rejects it on Gen2.
                    // Amplify sets owner from the signed-in identity; company group auth covers
                    // same-company admins reading/updating another user's sensitive row.
                    const createRes = await authClient.graphql({
                        query: createSensitiveUserInfoMutation,
                        variables: {
                            input: {
                                ...sensitiveInput,
                                cognitoId: targetCognitoId,
                                companyId,
                            },
                        },
                    });
                    const created = (createRes as { data?: { createSensitiveUserInfo?: SensitiveUserInfo } })
                        .data?.createSensitiveUserInfo;
                    setSensitiveInfo(
                        created ??
                            ({
                                id: userId,
                                cognitoId: targetCognitoId,
                                companyId,
                                ...sensitiveUpdates,
                            } as SensitiveUserInfo),
                    );
                };

                if (existingSensitive) {
                    try {
                        await runUpdate();
                    } catch (updateErr) {
                        if (!isConditionalRequestFailure(updateErr)) throw updateErr;
                        await runCreate();
                    }
                } else {
                    try {
                        await runCreate();
                    } catch (createErr) {
                        if (!isConditionalRequestFailure(createErr)) throw createErr;
                        await runUpdate();
                    }
                }
            }

            // Create change log entries — non-blocking so a log failure does not undo a successful save
            for (const change of changesForLog) {
                try {
                    const logInput = generateChangeLogEntry(
                        userId,
                        companyId,
                        change.field,
                        toChangeLogValue(change.oldValue),
                        toChangeLogValue(change.newValue),
                        changedBy,
                        changedByName,
                    );
                    await createChangeLogEntry(logInput);
                } catch (logErr) {
                    console.warn('Change log entry failed:', logErr);
                    Sentry.captureException(logErr);
                }
            }

            // Update local profileUser state to reflect changes
            if (Object.keys(mergedUserUpdates).length > 0) {
                setProfileUser((prev) => (prev ? { ...prev, ...mergedUserUpdates } : prev));
                setAllCompanyUsers(
                    allCompanyUsers.map((u) => (u.id === userId ? { ...u, ...mergedUserUpdates } as User : u)),
                );

                if (companyUser?.id === userId) {
                    setCompanyUser({ ...companyUser, ...mergedUserUpdates } as User);
                }

                if (companyOwner?.id === userId) {
                    setCompanyOwner({ ...companyOwner, ...mergedUserUpdates } as CompanyOwner);
                }

                if (typeof mergedUserUpdates.userPhoto === 'string') {
                    await loadPhoto(mergedUserUpdates.userPhoto);
                }
            }

            setPendingPhotoFile(null);
            pendingChangesRef.current = [];
            setIsEditing(false);
        } catch (err) {
            console.error("Error saving profile changes:", err);
            Sentry.captureException(err);
            const message = getGraphQlErrorMessage(err) || 'Failed to save changes. Please try again.';
            setSaveError(message);
        } finally {
            setIsSaving(false);
        }
    };

    if (!profileUser) {
        return (
            <div className="page-container sup-page">
                <div className="sup-loading">Loading user profile...</div>
            </div>
        );
    }

    const profilePhotoPreviewUrl = pendingPhotoPreviewUrl || photoUrl;

    return (
        <div className="page-container sup-page">
            {/* Back Link */}
            <BackButton to="/users" buttonText="Back to Users" />

            {/* Header Section */}
            <div className="sup-header">
                <div className="sup-header-content">
                    <div className="sup-avatar">
                        {profilePhotoPreviewUrl ? (
                            <img src={profilePhotoPreviewUrl} alt={profileUser.name || "User"} />
                        ) : (
                            <span className="sup-avatar-initials">
                                {getInitials(profileUser.name)}
                            </span>
                        )}
                    </div>
                    <div className="sup-header-info">
                        <h1 className="sup-user-name">{profileUser.name || "Unnamed User"}</h1>
                        <div className="sup-header-meta">
                            <span className={`sup-role-badge ${getRoleBadgeClass(profileUser.role)}`}>
                                {formatRoleName(profileUser.role)}
                            </span>
                            {profileUser.currentJobClassification && (
                                <span className="sup-job-classification">
                                    {profileUser.currentJobClassification}
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="sup-header-actions">
                        {canEditThisProfile && !isEditing && (
                            <button className="sup-btn-edit" onClick={handleEditProfile}>
                                Edit Profile
                            </button>
                        )}
                    </div>
                </div>

                {/* Tab Bar */}
                <div className="sup-tabs">
                    {visibleTabs.map((tab) => (
                        <button
                            key={tab.id}
                            className={`sup-tab ${activeTab === tab.id ? "active" : ""}`}
                            onClick={() => selectTab(tab.id)}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Save/Discard Bar */}
            {isEditing && (
                <div className="sup-edit-bar">
                    <span className="sup-edit-bar-text">Editing mode — make changes below</span>
                    {saveError && <span className="sup-edit-bar-error">{saveError}</span>}
                    <div className="sup-edit-bar-actions">
                        <button className="sup-btn-discard" onClick={handleDiscard}>
                            Close Edit Mode
                        </button>
                        <button className="sup-btn-save" onClick={handleSave} disabled={isSaving}>
                            {isSaving ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                </div>
            )}

            {/* Tab Content */}
            <div className="sup-tab-content">
                {activeTab === "personal" && (
                    <PersonalInfoTab
                        user={profileUser}
                        sensitiveInfo={sensitiveInfo}
                        isEditing={isEditing}
                        readOnly={false}
                        onFieldChange={handleFieldChange}
                        photoPreviewUrl={profilePhotoPreviewUrl}
                        onPhotoSelect={handlePhotoSelect}
                        isUploadingPhoto={isSaving}
                    />
                )}
                {activeTab === "employment" && (
                    <EmploymentTab
                        user={profileUser}
                        isEditing={isEditing}
                        readOnly={false}
                        onFieldChange={handleFieldChange}
                    />
                )}
                {activeTab === "safety-training" && (
                    <SafetyTrainingTab
                        user={profileUser}
                        readOnly={false}
                    />
                )}
                {activeTab === "skillsets" && (
                    <SkillsetsTab
                        user={profileUser}
                        readOnly={false}
                    />
                )}
                {activeTab === "media-files" && (
                    <MediaFilesTab
                        user={profileUser}
                        readOnly={false}
                    />
                )}
                {activeTab === "change-log" && (
                    <ChangeLogTab
                        user={profileUser}
                    />
                )}
                {canViewOrgChart && orgChartVisited && (
                    <div hidden={activeTab !== "org-chart"}>
                        <OrgChartTab focusUserId={profileUser.id} />
                    </div>
                )}
            </div>
        </div>
    );
};

/**
 * SingleUserProfilePage wraps the inner component with UserModuleProvider
 */
export const SingleUserProfilePage = () => {
    return (
        <UserModuleProvider>
            <SingleUserProfilePageInner />
        </UserModuleProvider>
    );
};
